'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  applyTheme,
  persistThemeId,
  resolvePersistedThemeId,
  THEME_CHANGE_EVENT,
  THEME_STORAGE_KEY,
} from '../themes/applyTheme';
import { DEFAULT_THEME_ID, getThemeById, THEMES } from '../themes/themes';
import type { ElvaTheme } from '../themes/themes';

function getDefaultTheme(): ElvaTheme {
  const theme = getThemeById(DEFAULT_THEME_ID);
  if (!theme) throw new Error(`Default Elva theme "${DEFAULT_THEME_ID}" is required`);
  return theme;
}

const DEFAULT_THEME = getDefaultTheme();

function resolveThemeId(id: string, fallback: string): string {
  if (getThemeById(id)) return id;
  if (getThemeById(fallback)) return fallback;
  return DEFAULT_THEME.id;
}

function subscribeToThemeStorage(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const onChange = () => onStoreChange();
  window.addEventListener('storage', onChange);
  window.addEventListener(THEME_CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
  };
}

interface ThemeContextValue {
  themeId: string;
  theme: ElvaTheme;
  setThemeId: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultThemeId?: string;
  /** Controlled theme id (optional). */
  themeId?: string;
  onThemeChange?: (id: string) => void;
  persist?: boolean;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultThemeId = DEFAULT_THEME_ID,
  themeId: controlledThemeId,
  onThemeChange,
  persist = false,
  storageKey = THEME_STORAGE_KEY,
}: ThemeProviderProps) {
  const [nonPersistThemeId, setNonPersistThemeId] = useState(() =>
    resolveThemeId(defaultThemeId, DEFAULT_THEME_ID),
  );

  const persistedThemeId = useSyncExternalStore(
    subscribeToThemeStorage,
    () => resolvePersistedThemeId(defaultThemeId, storageKey),
    () => resolveThemeId(defaultThemeId, DEFAULT_THEME_ID),
  );

  const themeId =
    controlledThemeId ?? (persist ? persistedThemeId : nonPersistThemeId);
  const theme = THEMES.find((t) => t.id === themeId) ?? DEFAULT_THEME;

  const setThemeId = useCallback(
    (id: string) => {
      const resolvedId = persist
        ? persistThemeId(id, defaultThemeId, storageKey)
        : resolveThemeId(id, defaultThemeId);

      if (controlledThemeId === undefined) {
        if (persist) {
          // useSyncExternalStore picks up THEME_CHANGE_EVENT from persistThemeId.
        } else {
          setNonPersistThemeId(resolvedId);
        }
      } else {
        onThemeChange?.(resolvedId);
      }

      applyTheme(resolvedId);
    },
    [controlledThemeId, defaultThemeId, onThemeChange, persist, storageKey],
  );

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Guard against external overrides of the theme tokens (e.g. a federated
  // remote that mounts its own ThemeProvider on the shared <html>). Whenever
  // the token block drifts from the selected theme, re-assert it.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }
    const el = document.documentElement;

    const isDrifted = () =>
      el.dataset.elvaTheme !== theme.id ||
      el.style.getPropertyValue('--cream-0') !== theme.page;

    const reassert = () => {
      if (isDrifted()) applyTheme(theme);
    };

    reassert();

    const observer = new MutationObserver(reassert);
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['style', 'class', 'data-elva-theme'],
    });

    return () => {
      observer.disconnect();
    };
  }, [theme]);

  const value = useMemo(
    () => ({ themeId, theme, setThemeId }),
    [themeId, theme, setThemeId],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
